import axiosInstance from '../utils/axios';

/**
 * 모바일 코드 정책 관련 API 서비스
 * UC-02 요구사항에 따른 모바일 코드 사용 통제 API 호출
 */
class MobileCodePolicyService {
    
    /**
     * 정책 목록 조회
     * @param {Object} filters - 필터링 옵션
     * @param {number} page - 페이지 번호
     * @param {number} limit - 페이지당 항목 수
     * @returns {Promise<Object>} API 응답
     */
    async getPolicies(filters = {}, page = 1, limit = 20) {
        try {
            // 백엔드 API 스펙에 맞게 파라미터 매핑
            const mappedFilters = {};
            if (filters.library_name) mappedFilters.library_name = filters.library_name;
            if (filters.security_level) mappedFilters.security_level = filters.security_level;
            if (filters.is_allowed !== '' && filters.is_allowed != null) mappedFilters.is_allowed = filters.is_allowed;
            
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...mappedFilters
            });
            
            const response = await axiosInstance.get(`/mobile-code-policies?${params}`);
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data,
                    totalCount: response.data.totalCount || 0,
                    message: response.data.message
                };
            }
            
            return {
                success: false,
                message: response.data?.message || '정책 목록을 불러오는데 실패했습니다.'
            };
        } catch (error) {
            console.error('정책 목록 조회 오류:', error);
            return {
                success: false,
                message: error.response?.data?.message || '서버 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 정책 상세 조회
     * @param {number} id - 정책 ID
     * @returns {Promise<Object>} API 응답
     */
    async getPolicyDetail(id) {
        try {
            const response = await axiosInstance.get(`/mobile-code-policies/${id}`);
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data,
                    message: response.data.message
                };
            }
            
            return {
                success: false,
                message: response.data?.message || '정책 상세 정보를 불러오는데 실패했습니다.'
            };
        } catch (error) {
            console.error('정책 상세 조회 오류:', error);
            return {
                success: false,
                message: error.response?.data?.message || '서버 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 정책 생성
     * @param {Object} policyData - 정책 데이터
     * @returns {Promise<Object>} API 응답
     */
    async createPolicy(policyData) {
        try {
            const response = await axiosInstance.post('/mobile-code-policies', policyData);
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data,
                    message: response.data.message || '정책이 성공적으로 생성되었습니다.'
                };
            }
            
            return {
                success: false,
                message: response.data?.message || '정책 생성에 실패했습니다.'
            };
        } catch (error) {
            console.error('정책 생성 오류:', error);
            return {
                success: false,
                message: error.response?.data?.message || '서버 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 정책 수정
     * @param {number} id - 정책 ID
     * @param {Object} policyData - 수정할 정책 데이터
     * @returns {Promise<Object>} API 응답
     */
    async updatePolicy(id, policyData) {
        try {
            const response = await axiosInstance.put(`/mobile-code-policies/${id}`, policyData);
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data,
                    message: response.data.message || '정책이 성공적으로 수정되었습니다.'
                };
            }
            
            return {
                success: false,
                message: response.data?.message || '정책 수정에 실패했습니다.'
            };
        } catch (error) {
            console.error('정책 수정 오류:', error);
            return {
                success: false,
                message: error.response?.data?.message || '서버 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 정책 삭제
     * @param {number} id - 정책 ID
     * @returns {Promise<Object>} API 응답
     */
    async deletePolicy(id) {
        try {
            const response = await axiosInstance.delete(`/mobile-code-policies/${id}`);
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    message: response.data.message || '정책이 성공적으로 삭제되었습니다.'
                };
            }
            
            return {
                success: false,
                message: response.data?.message || '정책 삭제에 실패했습니다.'
            };
        } catch (error) {
            console.error('정책 삭제 오류:', error);
            return {
                success: false,
                message: error.response?.data?.message || '서버 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 정책 통계 조회
     * @returns {Promise<Object>} API 응답
     */
    async getPolicyStatistics() {
        try {
            const response = await axiosInstance.get('/mobile-code-policies/stats');
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data,
                    message: response.data.message
                };
            }
            
            return {
                success: false,
                message: response.data?.message || '통계 정보를 불러오는데 실패했습니다.'
            };
        } catch (error) {
            console.error('정책 통계 조회 오류:', error);
            return {
                success: false,
                message: error.response?.data?.message || '서버 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 허용된 라이브러리 목록 조회
     * @returns {Promise<Object>} API 응답
     */
    async getAllowedLibraries() {
        try {
            const response = await axiosInstance.get('/mobile-code-policies/allowed');
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data,
                    message: response.data.message
                };
            }
            
            return {
                success: false,
                message: response.data?.message || '허용된 라이브러리 목록을 불러오는데 실패했습니다.'
            };
        } catch (error) {
            console.error('허용된 라이브러리 조회 오류:', error);
            return {
                success: false,
                message: error.response?.data?.message || '서버 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 보안 위반 내역 조회
     * @param {Object} filters - 필터링 옵션
     * @returns {Promise<Object>} API 응답
     */
    async getSecurityViolations(filters = {}) {
        try {
            const params = new URLSearchParams(
                Object.fromEntries(
                    Object.entries(filters).filter(([_, value]) => value != null && value !== '')
                )
            );
            
            const response = await axiosInstance.get(`/mobile-code-policies/security-violations?${params}`);
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data,
                    message: response.data.message
                };
            }
            
            return {
                success: false,
                message: response.data?.message || '보안 위반 내역을 불러오는데 실패했습니다.'
            };
        } catch (error) {
            console.error('보안 위반 내역 조회 오류:', error);
            return {
                success: false,
                message: error.response?.data?.message || '서버 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 정책 검증
     * @param {Object} policyData - 검증할 정책 데이터
     * @returns {Promise<Object>} API 응답
     */
    async validatePolicy(policyData) {
        try {
            const response = await axiosInstance.post('/mobile-code-policies/validate', policyData);
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data,
                    message: response.data.message
                };
            }
            
            return {
                success: false,
                message: response.data?.message || '정책 검증에 실패했습니다.'
            };
        } catch (error) {
            console.error('정책 검증 오류:', error);
            return {
                success: false,
                message: error.response?.data?.message || '서버 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 정책 일괄 삭제
     * @param {Array<number>} ids - 삭제할 정책 ID 배열
     * @returns {Promise<Object>} API 응답
     */
    async bulkDeletePolicies(ids) {
        try {
            const response = await axiosInstance.delete('/mobile-code-policies/bulk', {
                data: { ids }
            });
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data,
                    message: response.data.message || `${ids.length}개의 정책이 성공적으로 삭제되었습니다.`
                };
            }
            
            return {
                success: false,
                message: response.data?.message || '정책 일괄 삭제에 실패했습니다.'
            };
        } catch (error) {
            console.error('정책 일괄 삭제 오류:', error);
            return {
                success: false,
                message: error.response?.data?.message || '서버 오류가 발생했습니다.'
            };
        }
    }
}

const mobileCodePolicyService = new MobileCodePolicyService();
export default mobileCodePolicyService;
